import { logger } from './logger';
import { monitoring } from './monitoring';

/**
 * Service Worker registration and management
 */
export class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private updateInterval: NodeJS.Timeout | null = null;

  /**
   * Register the service worker
   */
  async register(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      logger.info('Service Worker not supported');
      return;
    }

    // Don't register in development unless explicitly enabled
    if (import.meta.env.DEV && !import.meta.env.VITE_ENABLE_SW) {
      logger.debug('Service Worker disabled in development');
      return;
    }

    // Don't register in Vercel preview deployments
    if (window.location.hostname.includes('vercel.app')) {
      logger.debug('Service Worker disabled in preview deployment');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/',
      });

      this.registration = registration;
      logger.info('Service Worker registered successfully');

      // Track registration success
      monitoring.trackEvent('service_worker_registered', {
        scope: registration.scope,
      });

      // Set up event listeners
      this.setupEventListeners(registration);

      // Check for updates periodically (every 30 minutes)
      this.updateInterval = setInterval(
        () => {
          this.checkForUpdates();
        },
        30 * 60 * 1000,
      );

      // Check for updates on visibility change
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          this.checkForUpdates();
        }
      });
    } catch (error) {
      logger.error('Service Worker registration failed:', error);
      monitoring.reportError(error as Error, {
        action: 'service_worker_registration',
      });
    }
  }

  /**
   * Set up service worker event listeners
   */
  private setupEventListeners(registration: ServiceWorkerRegistration): void {
    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const installingWorker = registration.installing;
      if (!installingWorker) return;

      logger.info('Service Worker update found');

      installingWorker.addEventListener('statechange', () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New update available
            this.notifyUpdateAvailable();
          } else {
            // First install
            logger.info('Service Worker installed for the first time');
          }
        }
      });
    });

    // Listen for controller change
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      logger.info('Service Worker controller changed');
      // Reload to ensure consistency
      window.location.reload();
    });

    // Handle messages from service worker
    navigator.serviceWorker.addEventListener('message', event => {
      this.handleServiceWorkerMessage(event);
    });
  }

  /**
   * Check for service worker updates
   */
  async checkForUpdates(): Promise<void> {
    if (!this.registration) return;

    try {
      await this.registration.update();
      logger.debug('Checked for Service Worker updates');
    } catch (error) {
      logger.error('Failed to check for updates:', error);
    }
  }

  /**
   * Notify user that an update is available
   */
  private notifyUpdateAvailable(): void {
    // This could trigger a toast or banner
    logger.info('New version available! Refresh to update.');

    // Track update available event
    monitoring.trackEvent('service_worker_update_available');

    // You could dispatch a custom event here for the UI to handle
    window.dispatchEvent(new CustomEvent('sw-update-available'));
  }

  /**
   * Skip waiting and activate new service worker
   */
  async skipWaiting(): Promise<void> {
    if (!this.registration?.waiting) return;

    // Tell the waiting service worker to skip waiting
    this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });

    // Track update acceptance
    monitoring.trackEvent('service_worker_update_accepted');
  }

  /**
   * Handle messages from service worker
   */
  private handleServiceWorkerMessage(event: MessageEvent): void {
    const { data } = event;

    switch (data.type) {
      case 'CACHE_UPDATED':
        logger.debug('Cache updated:', data.payload);
        break;
      case 'OFFLINE_READY':
        logger.info('App ready for offline use');
        break;
      default:
        logger.debug('Service Worker message:', data);
    }
  }

  /**
   * Unregister the service worker
   */
  async unregister(): Promise<void> {
    if (!this.registration) return;

    try {
      const success = await this.registration.unregister();
      if (success) {
        logger.info('Service Worker unregistered');
        this.registration = null;
      }
    } catch (error) {
      logger.error('Failed to unregister Service Worker:', error);
    }

    // Clear update interval
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  /**
   * Clear all caches
   */
  async clearCaches(): Promise<void> {
    if (!('caches' in window)) return;

    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
      logger.info('All caches cleared');
    } catch (error) {
      logger.error('Failed to clear caches:', error);
    }
  }

  /**
   * Get cache storage estimate
   */
  async getCacheStorageEstimate(): Promise<StorageEstimate | null> {
    if (!('storage' in navigator) || !('estimate' in navigator.storage)) {
      return null;
    }

    try {
      const estimate = await navigator.storage.estimate();
      logger.debug('Storage estimate:', {
        usage: `${Math.round((estimate.usage || 0) / 1024 / 1024)}MB`,
        quota: `${Math.round((estimate.quota || 0) / 1024 / 1024)}MB`,
      });
      return estimate;
    } catch (error) {
      logger.error('Failed to get storage estimate:', error);
      return null;
    }
  }

  /**
   * Request persistent storage
   */
  async requestPersistentStorage(): Promise<boolean> {
    if (!('storage' in navigator) || !('persist' in navigator.storage)) {
      return false;
    }

    try {
      const isPersisted = await navigator.storage.persist();
      logger.info(`Persistent storage ${isPersisted ? 'granted' : 'denied'}`);
      return isPersisted;
    } catch (error) {
      logger.error('Failed to request persistent storage:', error);
      return false;
    }
  }
}

// Export singleton instance
export const serviceWorkerManager = new ServiceWorkerManager();

// Export helper to check if offline
export function isOffline(): boolean {
  return !navigator.onLine;
}

// Export helper to check if service worker is supported
export function isServiceWorkerSupported(): boolean {
  return 'serviceWorker' in navigator;
}
